import type { ExportJob } from '../api/contracts'
import { fetchExportFile } from './exportJobs'

type PrintFile = Pick<ExportJob, 'export_id' | 'file_name' | 'format'>

/**
 * How long a printed file's frame and address are kept: the print dialog
 * reads the file after `print()` returns, so it can't go at once. The next
 * print replaces it sooner.
 */
const PRINT_KEPT_MS = 10 * 60_000

let printed: { frame: HTMLIFrameElement; url: string; timer: number } | null = null

function clearPrinted(): void {
  if (!printed) return
  window.clearTimeout(printed.timer)
  printed.frame.remove()
  URL.revokeObjectURL(printed.url)
  printed = null
}

/**
 * Whether a PDF prints from a frame in the page: everywhere but Safari
 * (iPhone, iPad and the Mac), which prints the page around it or nothing.
 * There the PDF opens in a new tab, to print from its Share or File menu.
 */
export function canPrintInPage(
  userAgent: string = navigator.userAgent,
  touchPoints: number = navigator.maxTouchPoints
): boolean {
  if (/iPad|iPhone|iPod/.test(userAgent)) return false
  // iPadOS says it's a Mac, one with a touch screen.
  if (/Macintosh/.test(userAgent) && touchPoints > 1) return false
  return !/^((?!chrome|chromium|crios|fxios|edg|android).)*safari/i.test(userAgent)
}

/** "Ctrl+P", or "⌘P" on a Mac: how the person prints. */
export function printShortcutLabel(platform: string = navigator.platform): string {
  return /Mac|iPhone|iPad/.test(platform) ? '⌘P' : 'Ctrl+P'
}

/**
 * Print a finished PDF: read it, load it in a frame the person can't see,
 * and open the browser's print dialog for it. Where a page can't print a
 * frame (Safari), it opens in `tab` instead, a tab opened inside the click
 * (one opened later is blocked). False when it wasn't printed: no such tab,
 * or the browser wouldn't print the frame.
 */
export async function printExport(
  job: PrintFile,
  { tab }: { tab?: Window | null } = {}
): Promise<boolean> {
  const inPage = canPrintInPage()
  if (!inPage && !tab) return false
  const url = URL.createObjectURL(await fetchExportFile(job))
  if (!inPage && tab) {
    tab.location.href = url
    window.setTimeout(() => URL.revokeObjectURL(url), PRINT_KEPT_MS)
    return true
  }
  clearPrinted()
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.tabIndex = -1
  // Laid out but unseen: a frame that isn't laid out (display: none, no
  // size) never starts the browser's PDF viewer, so there'd be nothing to print.
  frame.style.cssText =
    'position:fixed;left:0;top:0;width:1px;height:100px;opacity:0;border:0;pointer-events:none'
  const loaded = new Promise<void>((resolve) => {
    frame.onload = () => resolve()
  })
  frame.src = url
  document.body.appendChild(frame)
  printed = { frame, url, timer: window.setTimeout(clearPrinted, PRINT_KEPT_MS) }
  await loaded
  try {
    frame.contentWindow?.focus()
    frame.contentWindow?.print()
    return true
  } catch {
    // The browser keeps the frame's viewer to itself (some Firefox versions).
    clearPrinted()
    return false
  }
}
